// Find and merge duplicate contacts (crm.manage).
//
// Duplicates here are structural, not carelessness. The seed and the sym25
// import minted `con-<name>`; the Entra directory sync minted
// `con-wpi-<alias>`; neither could match the other because the seed rows had
// no email. Eight WPI staff exist twice for that reason alone. Separately, a
// stale snapshot in the portal made saved contacts look lost, so people
// re-entered them (con-jeff-whitten / con-jeff-whitten-2, 39 seconds apart).
//
// REBUILT 2026-09-10 (Chauncey: "it should be a choice to merge it not an all
// or nothing… it should show all the data and go 1 at a time. instead here
// are 19 duplicates pick the best with limited information").
//
// What was wrong with the old screen, precisely:
//   1. Nineteen groups rendered at once, so the work read as a bulk chore
//      rather than nineteen separate decisions about real people.
//   2. Four fields per card — name, title, work email, added — and the answer
//      it asked for depended on fields it did not show. Both `con-sym25-
//      kager-kyle` and `con-sym25-kyle-kager` carry an invitation and a
//      document reference; nothing on screen said so.
//   3. The merge itself was all-or-nothing: whichever row you kept won every
//      contested field, and the other row's value was reachable only by
//      keeping THAT row instead — which then lost everything the first one
//      had. There was no way to say "his title, her email".
//
// So: one duplicate at a time, every field on the record shown side by side,
// and a per-field choice wherever the two disagree. The field list and the
// attachment counts both come from the API — the UI renders what the server
// says is choosable rather than keeping its own copy to drift.
//
// Deleting a contact is otherwise forbidden (Charles, 2026-07-28). A merge is
// the exception, and only as its tail: the losing row is written to
// crm.contact_merges in full first, so the person is never lost and the merge
// can be undone from that record.
//
// "NOT THE SAME PERSON" IS REMEMBERED (2026-09-29). Nothing but a merge used
// to be saved, so every pair a reviewer had ruled out was back on the next
// open — 15 of them for Robert, who reasonably read it as merged duplicates
// returning. Three outcomes now, and only two of them persist:
//   Not the same person   saved (crm.contact_duplicate_decisions); the pair
//                         never comes back, and Undo is offered at once
//   Skip / Decide later   this session only, exactly as before
//   Merge                 saved, as before
// The count of pairs marked different is always on screen: an answer the
// list is hiding must not look like there was nothing to answer.

import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Users, AlertTriangle, Check, ChevronLeft, ChevronRight, SkipForward, Equal, UserX, Undo2,
} from 'lucide-react'

import { Modal, Button, Badge, EmptyState } from '@/components/ui'
import { useApiToken } from '@/auth/useApiToken'
import {
  api, ApiError,
  type DuplicateGroup, type DuplicateContact, type MergeableField, type FieldChoice,
} from '@/api/client'
import { refreshDataset } from '@/store/refreshDataset'
import './MergeDuplicates.css'

const REASON_LABEL: Record<string, string> = {
  name: 'Same name',
  email: 'Same email address',
}

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'

const fullName = (c: DuplicateContact) => `${c.firstName} ${c.lastName}`.trim() || c.id

/** "Jane Doe and John Doe" / "A, B and C" — for the notices that name who was
 *  marked. */
const joinNames = (names: string[]) =>
  names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`

/** Same members, whatever the order or case — how the Undo finds the group it
 *  brought back. */
const memberSetKey = (ids: string[]) => ids.map((id) => id.toLowerCase()).sort().join('\n')

const pairsLabel = (n: number) => `${n} pair${n === 1 ? '' : 's'}`

/** One field's value as text. Objects (office address, social) are flattened
 *  rather than hidden — "show all the data" includes the awkward ones, and a
 *  reviewer choosing between two addresses has to see both. */
function displayValue(raw: unknown): string {
  if (raw === null || raw === undefined || raw === '') return ''
  if (typeof raw === 'boolean') return raw ? 'Yes' : 'No'
  if (Array.isArray(raw)) return raw.length ? raw.join(', ') : ''
  if (typeof raw === 'object') {
    const parts = Object.entries(raw as Record<string, unknown>)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .map(([k, v]) => `${k}: ${String(v)}`)
    return parts.join(' · ')
  }
  return String(raw)
}

const isEmpty = (v: string) => v.trim() === ''

interface FieldRow {
  key: string
  label: string
  survivor: string
  loser: string
  /** Both rows say the same thing — nothing to decide. */
  agrees: boolean
  /** Both rows have a value and they differ: the only genuine conflict. */
  contested: boolean
  /** Only the losing row has it — the classic fill-the-blank. */
  onlyLoser: boolean
}

/** Build the comparison table. Fields blank on BOTH records are dropped: a
 *  screen of eighty empty rows hides the six that matter. */
function buildRows(
  fields: MergeableField[], survivor: DuplicateContact, loser: DuplicateContact,
): FieldRow[] {
  const s = (survivor.record ?? {}) as Record<string, unknown>
  const l = (loser.record ?? {}) as Record<string, unknown>
  const out: FieldRow[] = []
  for (const f of fields) {
    const sv = displayValue(s[f.key])
    const lv = displayValue(l[f.key])
    if (isEmpty(sv) && isEmpty(lv)) continue
    const agrees = sv.trim().toLowerCase() === lv.trim().toLowerCase()
    out.push({
      key: f.key,
      label: f.label,
      survivor: sv,
      loser: lv,
      agrees,
      contested: !agrees && !isEmpty(sv) && !isEmpty(lv),
      onlyLoser: !agrees && isEmpty(sv) && !isEmpty(lv),
    })
  }
  return out
}

/** Two rows sharing an address but carrying DIFFERENT names are not
 *  necessarily one person: prod has `m@merry.gs` on both Merry Anderson and
 *  Merry Hagestad, which is either a shared household address or a bad import
 *  row — and merging them would delete a real investor. Some are genuine
 *  (`con-sym25-kager-kyle` / `con-sym25-kyle-kager` is one person imported
 *  twice with first and last name swapped), so this warns rather than blocks. */
function namesDisagree(g: DuplicateGroup): boolean {
  if (g.reason !== 'email') return false
  const key = (c: DuplicateContact) =>
    [c.firstName, c.lastName].map((v) => (v ?? '').trim().toLowerCase()).sort().join(' ')
  return new Set(g.contacts.map(key)).size > 1
}

/** What this record is attached to, in one line. The count a merge would
 *  MOVE is the honest measure of what a record is worth keeping. */
function AttachmentLine({ c }: { c: DuplicateContact }) {
  const a = c.attachments
  const bits: string[] = []
  if (a.invites) bits.push(`${a.invites} symposium invitation${a.invites === 1 ? '' : 's'}`)
  if (a.sends) bits.push(`${a.sends} email${a.sends === 1 ? '' : 's'} sent`)
  if (a.refs) bits.push(`${a.refs} task/note reference${a.refs === 1 ? '' : 's'}`)
  if (a.accountName) bits.push(a.accountName)
  return (
    <p className="mdup-attach">
      {bits.length ? bits.join(' · ') : <span className="dim">Nothing attached to this record</span>}
    </p>
  )
}

function RecordHeader({ c, keep, onKeep, side }: {
  c: DuplicateContact; keep: boolean; onKeep: () => void; side: string
}) {
  return (
    <label className={`mdup-pick${keep ? ' mdup-pick--keep' : ''}`}>
      <input type="radio" checked={keep} onChange={onKeep} className="mdup-radio" />
      <div className="mdup-pick-body">
        <div className="mdup-pick-head">
          <span className="mdup-side">{side}</span>
          <strong>{fullName(c)}</strong>
          {keep && <Badge tone="ok" variant="soft">Keeping this one</Badge>}
          {c.isWpi && <Badge tone="neutral" variant="outline">WPI staff</Badge>}
        </div>
        <AttachmentLine c={c} />
        <p className="mdup-meta">
          Added {fmtDate(c.createdAt)}{c.createdBy ? ` · ${c.createdBy}` : ''} · <code className="mdup-id">{c.id}</code>
        </p>
      </div>
    </label>
  )
}

export function MergeDuplicatesModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const getToken = useApiToken()
  const [groups, setGroups] = useState<DuplicateGroup[] | null>(null)
  const [fields, setFields] = useState<MergeableField[]>([])
  const [index, setIndex] = useState(0)
  const [keepId, setKeepId] = useState<string>('')
  const [mergeInId, setMergeInId] = useState<string>('')
  const [choices, setChoices] = useState<Record<string, FieldChoice>>({})
  const [showAgreed, setShowAgreed] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mergedCount, setMergedCount] = useState(0)
  // Pairs marked "not the same person" — server-counted, so it includes
  // other reviewers' decisions and earlier sessions.
  const [dismissedCount, setDismissedCount] = useState(0)
  const [hiddenGroups, setHiddenGroups] = useState(0)
  // The last "Not the same person" in this session, for its Undo: the group's
  // members (to find it again) and the pairs that click actually wrote.
  const [lastDismissed, setLastDismissed] = useState<{
    ids: string[]; names: string[]; pairs: Array<[string, string]>
  } | null>(null)

  // Returns the groups it loaded: after a merge the caller has to clamp the
  // position against the NEW list, and reading it from state would read the
  // pre-merge one.
  const load = useCallback(async (): Promise<DuplicateGroup[] | null> => {
    setError(null)
    try {
      const token = await getToken()
      const res = await api.duplicates(token)
      setGroups(res.groups)
      setFields(res.fields)
      setDismissedCount(res.dismissedCount ?? 0)
      setHiddenGroups(res.hiddenGroups ?? 0)
      return res.groups
    } catch (err) {
      setError(err instanceof ApiError && err.status === 403
        ? 'Merging duplicates requires the crm.manage permission.'
        : err instanceof Error ? err.message : String(err))
      return null
    }
  }, [getToken])

  useEffect(() => {
    if (!open) return
    setGroups(null); setIndex(0); setMergedCount(0); setError(null); setConfirming(false)
    setLastDismissed(null)
    void load()
  }, [open, load])

  const group = groups?.[index] ?? null

  // A default survivor is offered ONLY where the records agree that they are
  // one person. Where they disagree — different addresses, different accounts
  // — there is no default, because a pre-ticked answer on a screen you are
  // clicking through IS the accidental merge. Chauncey, 2026-08-27: "I just
  // don't want to have an accidental merge of contacts on thought, especially
  // with common last names."
  //
  // The default is now the row holding the MOST, not the row that looks
  // fullest: attachments are what a merge has to move, and moving fewer of
  // them is the safer direction.
  useEffect(() => {
    if (!group) return
    setChoices({})
    setShowAgreed(false)
    setConfirming(false)
    setMergeInId('')
    if (group.risk === 'high') { setKeepId(''); return }
    const weight = (c: DuplicateContact) =>
      c.attachments.sends * 100 + c.attachments.invites * 10 + c.attachments.refs
    setKeepId([...group.contacts].sort((a, b) => weight(b) - weight(a))[0]?.id ?? '')
  }, [group])

  const survivor = group?.contacts.find((c) => c.id === keepId) ?? null
  // One losing row per confirmation. A three-way duplicate is two merges and
  // two confirmations — batching them would mean confirming a sentence that
  // only described the first. Which of the others goes first is the
  // reviewer's, not the array order's.
  const others = useMemo(
    () => (group?.contacts ?? []).filter((c) => c.id !== keepId),
    [group, keepId],
  )
  const loser = others.find((c) => c.id === mergeInId) ?? others[0] ?? null
  // Records beyond one pair. From the GROUP, not from `others`: with nothing
  // picked to keep (a high-risk group), `others` is every record, and a plain
  // two-record group would have been told it was "more than one decision".
  const extraRows = Math.max(0, (group?.contacts.length ?? 0) - 2)

  const rows = useMemo(
    () => (survivor && loser ? buildRows(fields, survivor, loser) : []),
    [fields, survivor, loser],
  )
  const contested = rows.filter((r) => r.contested)
  const agreed = rows.filter((r) => r.agrees)
  const differing = rows.filter((r) => !r.agrees)
  // Every contested field must be answered. A blank left unanswered is fine —
  // the default fills it — but a genuine conflict silently resolved in the
  // survivor's favour is exactly the all-or-nothing behaviour being removed.
  const unanswered = contested.filter((r) => !choices[r.key])

  const choose = (key: string, side: FieldChoice) =>
    setChoices((c) => ({ ...c, [key]: side }))

  const runMerge = async () => {
    if (!group || !survivor || !loser) return
    setBusy(true); setError(null)
    try {
      const token = await getToken()
      await api.mergeContacts(token, survivor.id, loser.id, 'duplicate', choices)
      setConfirming(false)
      setMergedCount((n) => n + 1)
      // The merge may have taken one of the marked records with it (the
      // server drops a merged record's decisions), so the Undo is withdrawn.
      setLastDismissed(null)
      // The store still holds the pre-merge rows — pull the real state back,
      // then re-detect: this merge may have resolved or reshaped later groups,
      // so the list is rebuilt rather than spliced.
      await refreshDataset()
      const fresh = await load()
      setIndex((i) => Math.max(0, Math.min(i, (fresh?.length ?? 1) - 1)))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  // "Not the same person": every pair in the group is saved as two different
  // people. The group then drops out of the reloaded list, so the same
  // position shows the next one — the action advances, like a merge does.
  const markNotSame = async () => {
    if (!group) return
    const ids = group.contacts.map((c) => c.id)
    setBusy(true); setError(null)
    try {
      const token = await getToken()
      const res = await api.dismissDuplicates(token, ids)
      setLastDismissed({ ids, names: group.contacts.map(fullName), pairs: res.newPairs ?? [] })
      const fresh = await load()
      setIndex((i) => Math.max(0, Math.min(i, (fresh?.length ?? 1) - 1)))
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  // Undo the last "Not the same person" and go straight back to that group.
  // Only the pairs that click wrote are taken back: in a three-record group a
  // pair decided earlier (by anyone) stays decided.
  const undoNotSame = async () => {
    if (!lastDismissed) return
    setBusy(true); setError(null)
    try {
      const token = await getToken()
      for (const pair of lastDismissed.pairs) {
        await api.undismissDuplicates(token, pair)
      }
      const want = memberSetKey(lastDismissed.ids)
      setLastDismissed(null)
      const fresh = await load()
      const at = (fresh ?? []).findIndex((g) => memberSetKey(g.contacts.map((c) => c.id)) === want)
      if (at >= 0) setIndex(at)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const total = groups?.length ?? 0
  const atEnd = index >= total - 1
  // Group-level wording: with three or more records the button speaks for
  // every pair among them, and says so.
  const notSameLabel = group && group.contacts.length > 2
    ? 'None of these are the same person'
    : 'Not the same person'
  // Already-decided pairs inside a (3+) group, by name — so nobody decides
  // them twice.
  const decidedHere = (group?.dismissedPairs ?? []).map(([a, b]) => {
    const name = (id: string) => {
      const c = group?.contacts.find((x) => x.id.toLowerCase() === id.toLowerCase())
      return c ? fullName(c) : id
    }
    return `${name(a)} and ${name(b)}`
  })

  /** Always visible below the records: the third outcome, beside Decide
   *  later and Merge. Rendered even when no record is picked to keep — a
   *  high-risk group has no default precisely because it may be two people. */
  const notSameButton = (
    <Button variant="default" icon={<UserX size={14} />} disabled={busy} onClick={() => void markNotSame()}>
      {notSameLabel}
    </Button>
  )

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={980}
      title="Review duplicate contacts"
      footer={<Button variant="default" onClick={onClose}>Close</Button>}
    >
      {error && (
        <div className="mdup-error" role="alert">
          <AlertTriangle size={14} /> <span>{error}</span>
        </div>
      )}

      {lastDismissed && (
        <div className="mdup-done" role="status">
          <Check size={14} />
          <span>
            Marked <b>{joinNames(lastDismissed.names)}</b> as different people — they won’t be
            suggested again.
          </span>
          {/* Nothing to undo when every pair was already decided by someone
              else in the same moment — their decision is not this click's. */}
          {lastDismissed.pairs.length > 0 && (
            <Button variant="ghost" size="sm" icon={<Undo2 size={14} />} disabled={busy} onClick={() => void undoNotSame()}>
              Undo
            </Button>
          )}
        </div>
      )}

      {groups === null && !error && <p className="mdup-status">Looking for duplicates…</p>}

      {groups !== null && total === 0 && (
        <EmptyState
          icon={<Check size={32} />}
          title={mergedCount ? 'All merged' : hiddenGroups ? 'Nothing left to review' : 'No duplicates found'}
          hint={[
            mergedCount
              ? `${mergedCount} duplicate ${mergedCount === 1 ? 'pair' : 'pairs'} merged.`
              : hiddenGroups
                ? 'Every record that shares a name or an email address with another has been decided.'
                : 'Nothing in All Contacts shares a name or an email address with another record.',
            dismissedCount
              ? `${pairsLabel(dismissedCount)} marked as different people ${dismissedCount === 1 ? 'is' : 'are'} not shown.`
              : '',
          ].filter(Boolean).join(' ')}
        />
      )}

      {/* Gated on the GROUP only. It used to require a survivor as well, and a
          high-risk group deliberately has none (nothing is pre-selected when
          the records may be two people) — so exactly those groups rendered an
          empty body: no records to pick from, no Skip, no way on. */}
      {group && (
        <>
          <nav className="mdup-nav">
            <Button
              variant="default" size="sm" icon={<ChevronLeft size={14} />}
              disabled={index === 0 || busy}
              onClick={() => setIndex((i) => i - 1)}
            >
              Previous
            </Button>
            <span className="mdup-counter">
              <Users size={13} /> Duplicate <b>{index + 1}</b> of {total}
              {mergedCount > 0 && <span className="dim"> · {mergedCount} merged</span>}
            </span>
            <Button
              variant="default" size="sm" icon={<SkipForward size={14} />}
              disabled={atEnd || busy}
              onClick={() => setIndex((i) => i + 1)}
            >
              Skip
            </Button>
          </nav>
          {dismissedCount > 0 && (
            <p className="mdup-quiet">
              {pairsLabel(dismissedCount)} marked as different people {dismissedCount === 1 ? 'is' : 'are'} not
              shown here.
            </p>
          )}

          <header className="mdup-group-head">
            <Badge tone="warn" variant="soft">{REASON_LABEL[group.reason] ?? group.reason}</Badge>
            <span className="mdup-group-name">
              {group.contacts.map((c) => fullName(c)).join('  ·  ')}
            </span>
          </header>

          {group.risk === 'high' && (
            <p className="mdup-danger" role="note">
              <AlertTriangle size={13} />
              <span>
                <b>These may be two different people.</b> {group.riskReason} Nothing is
                pre-selected here on purpose — read both records before choosing. If they are
                different people, say so below and this pair will not be suggested again.
              </span>
            </p>
          )}
          {namesDisagree(group) && (
            <p className="mdup-warn" role="note">
              <AlertTriangle size={13} />
              <span>
                These share an email address but have <b>different names</b>. That can be one
                person imported twice with the names swapped — or two people (often a couple)
                using one address. Check before merging: merging two different people removes
                one of them from the list. If they are two people, choose <b>{notSameLabel}</b>{' '}
                below and the review will stop suggesting them.
              </span>
            </p>
          )}
          {extraRows > 0 && (
            <p className="mdup-warn" role="note">
              <AlertTriangle size={13} />
              <span>
                This group has <b>{group.contacts.length} records</b>, so it is more than one
                decision. Merge them a pair at a time; whatever is left comes back to this screen
                afterwards. <b>{notSameLabel}</b> marks every pair among them as different people.
              </span>
            </p>
          )}
          {decidedHere.length > 0 && (
            <p className="mdup-quiet" role="note">
              Already marked as different people: {decidedHere.join('; ')}.
            </p>
          )}

          <div className="mdup-picks">
            {group.contacts.map((c, i) => (
              <RecordHeader
                key={c.id}
                c={c}
                side={`Record ${String.fromCharCode(65 + i)}`}
                keep={keepId === c.id}
                onKeep={() => setKeepId(c.id)}
              />
            ))}
          </div>

          {keepId && loser && others.length > 1 && (
            <p className="mdup-mergein">
              Merging in{' '}
              <select value={loser.id} onChange={(e) => setMergeInId(e.target.value)}>
                {others.map((c) => (
                  <option key={c.id} value={c.id}>{fullName(c)} · {c.id}</option>
                ))}
              </select>
              {' '}first — the {others.length === 2 ? 'other one' : `other ${others.length - 1}`} stay
              {others.length === 2 ? 's' : ''} for a separate decision.
            </p>
          )}

          {!survivor || !loser ? (
            <div className="mdup-actions mdup-actions--list">
              <span className="mdup-pick-first">
                To merge, choose which record to keep, then decide the fields below.
              </span>
              {notSameButton}
              <Button
                variant="default" icon={<ChevronRight size={14} />}
                disabled={atEnd || busy}
                onClick={() => setIndex((i) => i + 1)}
              >
                Decide later
              </Button>
            </div>
          ) : (
            <>
              <div className="mdup-fieldhead">
                <h4>
                  Every field on both records
                  {contested.length > 0 && (
                    <Badge tone={unanswered.length ? 'warn' : 'ok'} variant="soft">
                      {unanswered.length
                        ? `${unanswered.length} still to decide`
                        : `${contested.length} decided`}
                    </Badge>
                  )}
                </h4>
                <p className="mdup-fieldhint">
                  Where only one record has a value it is carried over. Where both have one and
                  they differ, pick the one to keep — that choice is the merge, not the record you
                  picked above. Symposium years, children and track memberships are always
                  combined, never dropped.
                </p>
              </div>

              <table className="mdup-table">
                <thead>
                  <tr>
                    <th className="mdup-th-label">Field</th>
                    <th>{fullName(survivor)} <span className="dim">(keeping)</span></th>
                    <th>{fullName(loser)} <span className="dim">(merging in)</span></th>
                  </tr>
                </thead>
                <tbody>
                  {differing.map((r) => {
                    const picked = choices[r.key]
                    // A blank on the survivor with a value on the loser has an
                    // obvious answer, and the server applies it by default —
                    // it is shown, and still overridable, but not demanded.
                    const survivorWins = picked === 'survivor' || (!picked && !r.onlyLoser)
                    const loserWins = picked === 'loser' || (!picked && r.onlyLoser)
                    return (
                      <tr key={r.key} className={r.contested && !picked ? 'mdup-row--todo' : undefined}>
                        <th scope="row">
                          {r.label}
                          {r.contested && !picked && <span className="mdup-todo-dot" aria-label="undecided" />}
                        </th>
                        <td>
                          <label className={`mdup-cell${survivorWins ? ' mdup-cell--win' : ''}`}>
                            <input
                              type="radio"
                              name={`f-${r.key}`}
                              checked={survivorWins}
                              onChange={() => choose(r.key, 'survivor')}
                            />
                            <span>{r.survivor || <em className="dim">empty</em>}</span>
                          </label>
                        </td>
                        <td>
                          <label className={`mdup-cell${loserWins ? ' mdup-cell--win' : ''}`}>
                            <input
                              type="radio"
                              name={`f-${r.key}`}
                              checked={loserWins}
                              onChange={() => choose(r.key, 'loser')}
                              disabled={!r.loser}
                            />
                            <span>{r.loser || <em className="dim">empty</em>}</span>
                          </label>
                        </td>
                      </tr>
                    )
                  })}
                  {differing.length === 0 && (
                    <tr><td colSpan={3} className="mdup-nodiff">
                      <Equal size={13} /> These two records agree on every field they fill in.
                    </td></tr>
                  )}
                </tbody>
              </table>

              {agreed.length > 0 && (
                <>
                  <button className="mdup-toggle" onClick={() => setShowAgreed((v) => !v)}>
                    {showAgreed ? 'Hide' : 'Show'} the {agreed.length} field{agreed.length === 1 ? '' : 's'} they agree on
                  </button>
                  {showAgreed && (
                    <table className="mdup-table mdup-table--agreed">
                      <tbody>
                        {agreed.map((r) => (
                          <tr key={r.key}>
                            <th scope="row">{r.label}</th>
                            <td colSpan={2}>{r.survivor}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              )}

              {confirming ? (
                <div className="mdup-confirm" role="alertdialog">
                  <p className="mdup-confirm-q">
                    Merge <b>{fullName(loser)}</b>
                    {loser.workEmail ? ` (${loser.workEmail})` : ' (no email)'}
                    {' '}into <b>{fullName(survivor)}</b>
                    {survivor.workEmail ? ` (${survivor.workEmail})` : ' (no email)'}?
                  </p>
                  <p className="mdup-confirm-what">
                    {loser.attachments.invites || loser.attachments.refs || loser.attachments.sends ? (
                      <>Their invitations, symposium history, tasks, notes and account links move across. </>
                    ) : null}
                    {Object.values(choices).filter((v) => v === 'loser').length > 0 && (
                      <>
                        <b>{Object.values(choices).filter((v) => v === 'loser').length} field
                        {Object.values(choices).filter((v) => v === 'loser').length === 1 ? '' : 's'}</b>
                        {' '}will take {fullName(loser)}’s value.{' '}
                      </>
                    )}
                    <b>{fullName(loser)}’s record is removed</b>. It is saved in full first, so this
                    can be undone.
                  </p>
                  <div className="mdup-actions">
                    <Button variant="default" disabled={busy} onClick={() => setConfirming(false)}>
                      Back
                    </Button>
                    <Button variant="primary" disabled={busy} onClick={() => void runMerge()}>
                      {busy ? 'Merging…' : 'Yes, merge them'}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mdup-actions mdup-actions--list">
                  {unanswered.length > 0 && (
                    <span className="mdup-pick-first">
                      {unanswered.length} field{unanswered.length === 1 ? '' : 's'} where both records
                      disagree still need{unanswered.length === 1 ? 's' : ''} an answer:{' '}
                      {unanswered.map((r) => r.label).join(', ')}.
                    </span>
                  )}
                  {notSameButton}
                  <Button
                    variant="default" icon={<ChevronRight size={14} />}
                    disabled={atEnd || busy}
                    onClick={() => setIndex((i) => i + 1)}
                  >
                    Decide later
                  </Button>
                  <Button
                    variant="primary"
                    disabled={busy || unanswered.length > 0}
                    onClick={() => setConfirming(true)}
                  >
                    Merge into {fullName(survivor)}
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}
    </Modal>
  )
}
